import { MOOD_IDS, MOODS, moodGradient, type Garment, type MoodId } from '@racksavant/core';
import { alpha } from '@racksavant/ui';
import type { CSSProperties } from 'react';

/**
 * Every surface in the studio can be tinted by whatever mood is in play.
 * Components read --rs-mood / --rs-mood-alt rather than hard-coding colour.
 */
export function moodStyle(moods: MoodId[] | MoodId | null | undefined): CSSProperties {
  const list = !moods ? [] : Array.isArray(moods) ? moods : [moods];
  const [a, b] = moodGradient(list);
  return {
    '--rs-mood': a,
    '--rs-mood-alt': b,
    '--rs-mood-glow': alpha(a, 0.32),
    '--rs-mood-veil': alpha(a, 0.12),
  } as CSSProperties;
}

export function moodLabel(mood: MoodId | null | undefined): string {
  return mood ? MOODS[mood].label : 'No mood yet';
}

export type MoodGarments = Record<MoodId, Garment[]>;

/**
 * A few real packshots per mood, taken off one page of the catalogue.
 *
 * The mood tiles argue with clothes rather than with adjectives, so every tile
 * needs two or three garments that actually carry that mood. Garments declare
 * their own `moods`, so one trending page fills every tile — fifteen requests
 * to draw one rail would be absurd.
 */
export function groupByMood(garments: Garment[], per = 3): MoodGarments {
  const grouped = {} as MoodGarments;
  for (const id of MOOD_IDS) grouped[id] = [];
  for (const garment of garments) {
    for (const mood of garment.moods) {
      const bucket = grouped[mood as MoodId];
      if (bucket && bucket.length < per) bucket.push(garment);
    }
  }
  return grouped;
}

/** 1–5 energy → a motion multiplier. Feral moves; minimal barely breathes. */
export function energyScale(moods: MoodId[]): number {
  if (moods.length === 0) return 1;
  const average = moods.reduce((sum, id) => sum + MOODS[id].energy, 0) / moods.length;
  return 0.7 + (average / 5) * 0.6;
}
